import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Users, Plus, Pencil, KeyRound } from 'lucide-react';
import { adminUsersOptions } from '../lib/resources/queries';
import { useDeactivateUser, useReactivateUser } from '../lib/resources/mutations';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { StatusPip, type Severity } from '../components/ui/status-pip';
import { EmptyState } from '../components/ui/empty-state';
import { formatMoney } from '../lib/utils';
import type { UserResponse } from '../lib/resources/types';
import { UserFormDialog } from './-components/user-form-dialog';
import { ResetPasswordDialog } from './-components/reset-password-dialog';

export const Route = createFileRoute('/admin/users')({
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(adminUsersOptions('ACTIVE')),
  component: AdminUsersPage,
});

function AdminUsersPage() {
  const [filter, setFilter] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
  const users = useSuspenseQuery(adminUsersOptions(filter)).data;
  const deactivate = useDeactivateUser();
  const reactivate = useReactivateUser();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<UserResponse | null>(null);
  const [resetTarget, setResetTarget] = useState<UserResponse | null>(null);

  return (
    <div className="max-w-5xl mx-auto p-8 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-[28px] font-bold tracking-tight">Users</h1>
        <div className="flex gap-2 items-center">
          <div className="flex gap-1">
            <Button size="sm" variant={filter === 'ACTIVE' ? 'primary' : 'secondary'} onClick={() => setFilter('ACTIVE')}>
              Active
            </Button>
            <Button size="sm" variant={filter === 'INACTIVE' ? 'primary' : 'secondary'} onClick={() => setFilter('INACTIVE')}>
              Inactive
            </Button>
          </div>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="size-3.5" /> New user
          </Button>
        </div>
      </div>
      <Card>
        <CardHeader><CardTitle>{filter === 'ACTIVE' ? 'Active users' : 'Inactive users'}</CardTitle></CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <EmptyState icon={Users}
              title={filter === 'ACTIVE' ? 'No users yet' : 'No deactivated users'}
              description={filter === 'ACTIVE'
                ? 'Click "New user" to add a contractor or another admin.'
                : ''} />
          ) : (
            <ul className="divide-y divide-border">
              {users.map((u) => {
                const sev: Severity = u.role === 'ADMIN' ? 'warn' : 'info';
                return (
                  <li key={u.id} className="flex items-center gap-4 py-3 text-[13px]">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{u.legalName}</div>
                      <div className="text-[11px] text-foreground-muted truncate">{u.email}</div>
                    </div>
                    <StatusPip severity={sev} label={u.role} />
                    <span className="font-mono text-[12px] text-foreground-muted w-24 truncate">{u.invoicePrefix}</span>
                    <span className="font-mono w-28 text-right">{formatMoney(u.baseSalaryCents)}</span>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditTarget(u)} title="Edit">
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setResetTarget(u)} title="Reset password">
                        <KeyRound className="size-3.5" />
                      </Button>
                      {u.status === 'ACTIVE' ? (
                        <Button size="sm" variant="ghost" onClick={() => deactivate.mutate(u.id)}>
                          Deactivate
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => reactivate.mutate(u.id)}>
                          Reactivate
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <UserFormDialog open={createOpen} onOpenChange={setCreateOpen} />
      <UserFormDialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)} user={editTarget} />
      <ResetPasswordDialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)} user={resetTarget} />
    </div>
  );
}
